export const DESKTOP_LATEST_URL = "/desktop/latest.json";
export const DESKTOP_DMG_URL = "/desktop/StockForgePlus.dmg";
export const DESKTOP_DMG_FILENAME = "StockForgePlus.dmg";

export type DesktopLatestInfo = {
  version: string;
  releasedAt?: string;
  dmg: string;
  versionedDmg?: string;
  intelDmg?: string | null;
};

export type DesktopUpdateStatus = {
  status: "dev" | "current" | "available" | "ready" | "fallback";
  version: string;
  current?: string;
  message?: string;
  downloadUrl?: string;
};

export type DesktopUpdateEvent = {
  type: "checking" | "available" | "not-available" | "downloaded" | "error";
  version?: string;
  message?: string;
};

export type SfeDesktopBridge = {
  isDesktop: true;
  inGridPane?: boolean;
  getInfo: () => Promise<{
    version: string;
    packaged: boolean;
    alwaysOnTop: boolean;
    updateReady: string | null;
  }>;
  setAlwaysOnTop: (value: boolean) => Promise<boolean>;
  checkForUpdates: () => Promise<DesktopUpdateStatus>;
  openExternal?: (url: string) => Promise<boolean>;
  quitAndInstall: () => Promise<boolean | { ok: boolean; message?: string; downloadUrl?: string; version?: string }>;
  onPinChanged?: (callback: (pinned: boolean) => void) => () => void;
  onUpdateEvent: (callback: (event: DesktopUpdateEvent) => void) => () => void;
  getGrid?: () => Promise<{ layout: string; cells: string[]; col: number; row: number; pinned: boolean; focused?: number }>;
  setGridLayout?: (layout: string) => Promise<unknown>;
  setGridSolo?: (on: boolean) => Promise<unknown>;
  setGridCell?: (index: number, kind: string) => Promise<unknown>;
  setGridFocus?: (index: number) => Promise<unknown>;
  openLegend?: () => Promise<unknown>;
  legendMenu?: () => Promise<{ widgets: Array<{ id: string; label: string }> }>;
  openLegendWidget?: (id: string) => Promise<unknown>;
  openLegendWidgets?: (payload: { ids: string[]; ticker?: string; chain?: "sell-put" | "sell-call" }) => Promise<unknown>;
  popupLegendMenu?: (pos: { x: number; y: number }) => Promise<unknown>;
  setGridSplits?: (col: number, row: number) => Promise<unknown>;
  beginGridDrag?: () => Promise<unknown>;
  endGridDrag?: () => Promise<unknown>;
  onGridChanged?: (callback: (state: { layout: string; cells: string[]; col: number; row: number; pinned: boolean; focused?: number }) => void) => () => void;
  onChromeContext?: (callback: (payload: { source?: string; index?: number; kind?: string }) => void) => () => void;
};

declare global {
  interface Window {
    sfeDesktop?: SfeDesktopBridge;
  }
}

export function isStockForgeDesktop(): boolean {
  return window.sfeDesktop?.isDesktop === true;
}

export function applyDesktopShellChrome(): void {
  if (!isStockForgeDesktop()) return;
  // Grid panes already have shell chrome (toolbar, pane header, window frame).
  // The standalone window styles add a second blue border and 78px traffic-light
  // inset, which makes StockForge+ look shifted next to Robinhood.
  if (window.sfeDesktop?.inGridPane) {
    document.documentElement.classList.add("sfe-desktop-pane");
    document.body.classList.add("sfe-desktop-pane");
    return;
  }
  document.documentElement.classList.add("sfe-desktop-shell");
  document.body.classList.add("sfe-desktop-shell");
}

export function getDesktopBridge(): SfeDesktopBridge | null {
  return window.sfeDesktop ?? null;
}

export async function fetchDesktopLatest(): Promise<DesktopLatestInfo | null> {
  try {
    const response = await fetch(DESKTOP_LATEST_URL, { cache: "no-store" });
    if (!response.ok) return null;
    const data = (await response.json()) as DesktopLatestInfo;
    if (!data || typeof data.version !== "string" || typeof data.dmg !== "string") return null;
    return data;
  } catch {
    return null;
  }
}

export function desktopSettingsHtml(latest: DesktopLatestInfo | null): string {
  const desktop = getDesktopBridge();
  if (desktop) {
    return `
      <section class="sfe-web-settings-card sfe-web-settings-card--full sfe-desktop-settings-card">
        <h4 class="sfe-web-settings-card-title">Mac app</h4>
        <p class="muted small sfe-column-settings-lead">You are in StockForge+ terminal. Click a pane, then use the title-bar app buttons to load StockForge+, Original Stock Forge, Robinhood, or Webull. <strong>Open in Legend</strong> on a row still opens that action’s Legend widgets stacked on the right. Use the layout icons and <strong>Pin</strong> on the far right. Site updates apply on reload; the terminal updates itself when a new build is published.</p>
        <p class="muted small" data-desktop-version>Shell v…</p>
        <div class="sfe-web-settings-proxy-actions">
          <button type="button" class="small" data-action="desktop-check-updates">Check for updates</button>
          <button type="button" class="small" data-action="desktop-quit-install" hidden>Restart to update</button>
        </div>
        <p class="muted small sfe-web-settings-status" data-desktop-status hidden></p>
      </section>`;
  }
  const dmg = latest?.dmg || DESKTOP_DMG_URL;
  const intel = latest?.intelDmg
    ? `<button type="button" class="small ghost" data-action="desktop-download" data-desktop-href="${latest.intelDmg}">Intel Mac</button>`
    : "";
  return `
    <section class="sfe-web-settings-card sfe-web-settings-mobile-card">
      <h4 class="sfe-web-settings-card-title">Mac app</h4>
      <p class="muted small sfe-column-settings-lead">Optional desktop window for StockForge+. Same site and login; can stay on top and update itself. After download, drag it to Applications. If macOS says it is damaged, that is the unsigned-download check — in Terminal run <code>xattr -cr "/Applications/StockForgePlus terminal.app"</code> then open the app again.</p>
      <div class="sfe-web-settings-proxy-actions">
        <a class="small sfe-desktop-download-link" href="${dmg}" download="${DESKTOP_DMG_FILENAME}">${desktopDownloadLabel(latest)}</a>
        ${intel}
      </div>
    </section>`;
}

function formatDesktopPublished(iso: string | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function desktopDownloadLabel(latest: DesktopLatestInfo | null): string {
  const parts: string[] = [];
  if (latest?.version) parts.push(`shell v${latest.version}`);
  const published = formatDesktopPublished(latest?.releasedAt);
  if (published) parts.push(published);
  return parts.length ? `Download for Mac (${parts.join(", ")})` : "Download for Mac";
}

export function startDesktopDmgDownload(href = DESKTOP_DMG_URL): void {
  const url = href.startsWith("http") ? href : new URL(href, window.location.origin).href;
  window.location.assign(url);
}

export function bindDesktopSettings(root: HTMLElement): () => void {
  const desktop = getDesktopBridge();
  if (!desktop) return () => {};

  const versionEl = root.querySelector<HTMLElement>("[data-desktop-version]");
  const status = root.querySelector<HTMLElement>("[data-desktop-status]");
  const restartBtn = root.querySelector<HTMLButtonElement>("[data-action=desktop-quit-install]");

  const setStatus = (message: string) => {
    if (!status) return;
    status.hidden = !message;
    status.textContent = message;
  };

  const showRestart = (version: string) => {
    if (restartBtn) restartBtn.hidden = false;
    setStatus(`Version ${version} is ready. Restart to install.`);
  };

  void desktop.getInfo().then((info) => {
    if (versionEl) versionEl.textContent = `Shell v${info.version}${info.packaged ? "" : " (dev)"}`;
    if (info.updateReady) showRestart(info.updateReady);
  });

  const onClick = (event: Event) => {
    const action = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-action]")?.dataset.action;
    if (action === "desktop-check-updates") {
      setStatus("Checking…");
      void desktop.checkForUpdates().then((result) => {
        if (result.status === "ready") {
          showRestart(result.version);
          return;
        }
        if (result.status === "available") {
          setStatus(`Version ${result.version} is downloading…`);
          return;
        }
        if (result.status === "fallback") {
          setStatus(result.message || "Could not apply an in-app update. Download the latest DMG from the site.");
          return;
        }
        if (result.status === "dev") {
          setStatus(result.message || "Updates run in the installed app.");
          return;
        }
        setStatus("You are on the latest shell.");
      });
    }
    if (action === "desktop-quit-install") {
      if (restartBtn) restartBtn.disabled = true;
      setStatus("Installing update…");
      void desktop.quitAndInstall().then((result) => {
        if (restartBtn) restartBtn.disabled = false;
        if (result === true) return;
        if (result && typeof result === "object") {
          if (result.ok) {
            setStatus("Restarting…");
            return;
          }
          setStatus(result.message || "Could not install the update. Download the latest DMG from the site.");
          return;
        }
        if (result === false) {
          setStatus("Could not install the update. Download the latest DMG from the site.");
        }
      }).catch((error) => {
        if (restartBtn) restartBtn.disabled = false;
        setStatus(error instanceof Error ? error.message : "Could not install the update.");
      });
    }
  };
  root.addEventListener("click", onClick);

  const stop = desktop.onUpdateEvent((event) => {
    if (event.type === "downloaded" && event.version) {
      showRestart(event.version);
    } else if (event.type === "available" && event.version) {
      setStatus(`Version ${event.version} is downloading…`);
    } else if (event.type === "error") {
      setStatus(event.message || "Update check failed.");
    }
  });

  return () => {
    root.removeEventListener("click", onClick);
    stop();
  };
}
